import {Trans} from '@lingui/macro';
import React, {FC, useMemo, useState} from 'react';
import styled from 'styled-components';

import {
  buildContactsManagerAccountsUrl,
  buildContactsManagerCompanyContactsUrl,
  buildContactsManagerSyncsUrl,
} from '../../../../../../../../src/helpers/contactsManagerHelpers';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncSourcesEnum,
  CrmSyncStatusesEnum,
} from '../../../../../../../../src/models/api/crmSyncModel';
import {
  generateNavigationBlockerId,
  NavigationBlockerId,
} from '../../../../../../../../src/store/panels/app/appPanelHelpers';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {useNavigationBlocking} from '../../../../../../../../srcCommon/app/navigationBlocking';
import {openInBrowser} from '../../../../../../../../srcCommon/browser/openInBrowser';
import {AsyncHandler, Handler, HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../../shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {Icon} from '../../../../../../../shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../../../../../shared/ui/src/theme/userThemeConsumer';
import {InfoBox} from '../../common/infobox/infoBox';
import {PageTab} from '../../common/pageTab';
import {PageWrapper} from '../../common/pageWrapper';
import {ContactsManagerHeader} from '../../contactsManagerHeader';
import {CrmSyncsErrors} from './crmSyncsErrors/crmSyncsErrors';
import {CrmSyncsFormModel} from './crmSyncsForm';
import {useCreateCrmSync, useUpdateCrmSync} from './crmSyncsHooks';
import {CrmSyncOptionsEnum} from './crmSyncsOptions/crmSyncsOptions';
import {enabledSources, useCrmPageConfig} from './crmSyncsPageConfig/crmSyncsPageConfig';
import {CrmSyncsPageContainerProps} from './crmSyncsPageContainer';
import {CrmSyncsScheduleInfoBoxContainer} from './crmSyncsScheduleInfoBox/crmSyncsScheduleInfoBoxContainer';
import {CrmSyncSettingsFormModel} from './crmSyncsSettings/crmSyncsSettings';
import {CrmSyncsSettingsContainer} from './crmSyncsSettings/crmSyncsSettingsContainer';
import {CrmSyncsUnsavedChangesDialog} from './crmSyncsUnsavedChangesDialog';

/*
 * Styles.
 */

const StyledInfoBoxWrapperDiv = styled.div`
  padding: 15px 53px 24px 0px;
  background: ${(p) => p.theme.greys.white};

  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StyledTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledErrorTitleDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/*
 * Component.
 */

export const CrmSyncsPage: FC<CrmSyncsPageContainerProps> = (props) => {
  const {
    panelId,
    source,
    previousPageType,
    isConnected,
    accountCrmSync,
    cardCrmSync,
    isOnErrorsPage,
    hasCrmSyncFullPageSalesforce,
    hasMicrosoftDynamicAccountSync,
    externalBaseUrl,
    isCrmSyncLoading,
    navigateToLink,
  } = props;

  const {
    logo,
    helpCenterLink,
    displayName,
    card: cardConfig,
    account: accountConfig,
  } = useCrmPageConfig(source);

  const [updatedSettingFormState, setUpdatedSettingsFormState] = useState<
    CrmSyncSettingsFormModel | undefined
  >(undefined);
  const cardForm = updatedSettingFormState?.card;
  const accountForm = updatedSettingFormState?.account;

  useCreateCrmSync(source, isConnected, isCrmSyncLoading, Boolean(accountCrmSync), Boolean(cardCrmSync));

  const isAccountSyncEnabledOnServer = accountCrmSync?.status === CrmSyncStatusesEnum.ENABLED;
  const isCardSyncEnabledOnServer = cardCrmSync?.status === CrmSyncStatusesEnum.ENABLED;
  const {isUpdatingCrmSync: isUpdatingAccountCrmSync, onUpdateCrmSync: onUpdateAccountCrmSync} =
    useUpdateCrmSync(
      source,
      CrmSyncIntegrationResourceTypesEnum.ACCOUNT,
      isAccountSyncEnabledOnServer,
      accountCrmSync?.id,
      updatedSettingFormState,
    );

  const {isUpdatingCrmSync: isUpdatingCardCrmSync, onUpdateCrmSync: onUpdateCardCrmSync} = useUpdateCrmSync(
    source,
    CrmSyncIntegrationResourceTypesEnum.CARD,
    isCardSyncEnabledOnServer,
    cardCrmSync?.id,
    updatedSettingFormState,
  );

  const hasAccountFormChange = Boolean(accountForm);
  const hasCardFormChange = Boolean(cardForm);

  const isFormInvalid = updatedSettingFormState?.isFormInvalid;

  const canSyncAccount = computeCanSync(isUpdatingAccountCrmSync, !isCrmSyncLoading, accountForm);
  const canSyncCard = computeCanSync(isUpdatingCardCrmSync, !isCrmSyncLoading, cardForm);
  const canSync =
    accountConfig && cardConfig
      ? canSyncCard && canSyncAccount && !isFormInvalid
      : canSyncAccount && !isFormInvalid;
  const canSyncOrSave = (hasAccountFormChange || hasCardFormChange) && canSync;

  // generate our own id so we do not block navigation when you click save/sync
  const navigationBlockerId = useMemo(() => generateNavigationBlockerId(), []);
  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(
    panelId,
    canSyncOrSave,
    navigationBlockerId,
  );

  const onSaveOrSync = async () => {
    await onUpdateAccountCrmSync();
    await onUpdateCardCrmSync();
    if (updatedSettingFormState) {
      // clear pending changes since we saved
      setUpdatedSettingsFormState(undefined);
    }
  };

  const navigateToPreviousPage = (ignoreNavigationBlockerId?: NavigationBlockerId) => {
    // TODO: PB-38464 This doesn't work correctly when navigating between settings and errors page
    // should fix before launching contacts sync
    if (previousPageType === ContactsManagerParametersTypesEnum.COMPANY_CONTACTS) {
      navigateToLink(buildContactsManagerCompanyContactsUrl(), undefined, ignoreNavigationBlockerId);
      return;
    }
    navigateToLink(buildContactsManagerAccountsUrl(), undefined, ignoreNavigationBlockerId);
  };

  // navigate away if we don't have the feature flag or correct source
  if (
    !enabledSources.includes(source) ||
    (source === CrmSyncSourcesEnum.SALESFORCE && !hasCrmSyncFullPageSalesforce) ||
    (source === CrmSyncSourcesEnum.MICROSOFT_DYNAMICS && !hasMicrosoftDynamicAccountSync)
  ) {
    navigateToPreviousPage();
  }

  const isCardSyncEnabled = computeIsSyncEnabled(isCardSyncEnabledOnServer, cardForm);
  const isAccountSyncEnabled = computeIsSyncEnabled(isAccountSyncEnabledOnServer, accountForm);
  const canSyncNow = (isCardSyncEnabled || isAccountSyncEnabled) && canSync;

  const shouldHideFooter = isOnErrorsPage || isCrmSyncLoading;

  const hasAccountSyncErrors = Boolean(accountCrmSync?.errors && accountCrmSync.errors.length > 0);
  const hasCardSyncErrors = Boolean(cardCrmSync?.errors && cardCrmSync.errors.length > 0);
  const hasSyncErrors = hasAccountSyncErrors || hasCardSyncErrors;

  if (!isCrmSyncLoading && isOnErrorsPage && !hasSyncErrors) {
    // redirect from errors page if there are no errors to show
    navigateToLink(buildContactsManagerSyncsUrl(source));
  }

  return (
    <PageWrapper
      header={renderHeader(logo, displayName, navigateToPreviousPage, previousPageType)}
      pageTabs={!hasSyncErrors ? null : renderPageTabs(source, isOnErrorsPage, navigateToLink)}
      footer={
        shouldHideFooter
          ? null
          : renderFooter(
              canSyncOrSave,
              isAccountSyncEnabledOnServer || isCardSyncEnabledOnServer,
              onSaveOrSync,
              () => navigateToPreviousPage(navigationBlockerId), // we don't want to block navigation when you click sync
            )
      }
      sideContent={renderInfoBoxes(
        source,
        helpCenterLink,
        displayName,
        computeShouldShowNextSync(
          isAccountSyncEnabledOnServer,
          isCardSyncEnabledOnServer,
          updatedSettingFormState,
        ),
        canSyncNow,
        onSaveOrSync,
      )}
    >
      <>
        {isOnErrorsPage ? (
          <CrmSyncsErrors
            accountSyncErrors={accountCrmSync?.errors || []}
            cardSyncErrors={cardCrmSync?.errors || []}
            source={source}
            externalBaseUrl={externalBaseUrl}
          />
        ) : (
          <CrmSyncsSettingsContainer
            panelId={panelId}
            source={source}
            isSaving={isUpdatingAccountCrmSync || isUpdatingCardCrmSync}
            onChange={setUpdatedSettingsFormState}
          />
        )}
        {didBlockNavigation && (
          <CrmSyncsUnsavedChangesDialog
            onClose={cancelNavigation}
            onSave={() => {
              onSaveOrSync();
              unblockNavigation();
            }}
          />
        )}
      </>
    </PageWrapper>
  );
};

/*
 * Helpers.
 */

function isListViewValid(formState?: CrmSyncsFormModel) {
  if (!formState) {
    return true;
  }
  // if they chose the list option make sure they have selected a list id
  if (formState?.selectedListOption.type === CrmSyncOptionsEnum.LISTS) {
    return Boolean(formState?.selectedListOption.listId);
  }
  return Boolean(formState?.selectedListOption.type);
}

function isMappingValid(formState?: CrmSyncsFormModel) {
  if (!formState) {
    return true;
  }

  // if we encounter an undefined customFieldId the mapping is not valid
  return !formState.mappedFields.some((field) => field.customFieldId === undefined);
}

function renderBreadcrumbLabel(previousPage?: ContactsManagerParametersTypesEnum) {
  if (previousPage === ContactsManagerParametersTypesEnum.COMPANY_CONTACTS) {
    return <Trans id="crm-sync-page-breadcrumb-label-company-contacts">Company contacts</Trans>;
  }
  return <Trans id="crm-sync-page-breadcrumb-label-accounts">Accounts</Trans>;
}

function renderHeader(
  logo: string,
  displayName: string,
  onBreadcrumbClick: Handler,
  previousPage?: ContactsManagerParametersTypesEnum,
) {
  return (
    <ContactsManagerHeader
      title={
        <StyledTitle>
          <img src={logo} alt="crm logo" />
          <Trans id="crm-sync-page-title">Sync from {displayName}</Trans>
        </StyledTitle>
      }
      topLevelBreadcrumb={{
        label: renderBreadcrumbLabel(previousPage),
        onBreadcrumbClick,
      }}
      interactionId="crm-sync-page-header"
    />
  );
}

function renderPageTabs(
  source: CrmSyncSourcesEnum,
  isOnErrorsPage: boolean,
  navigateToLink: HandlerOf<string>,
) {
  return (
    <>
      <PageTab
        title={<Trans id="crm-syncs-page-sync-settings-tab-title">Sync settings</Trans>}
        isSelected={!isOnErrorsPage}
        onClick={() => navigateToLink(buildContactsManagerSyncsUrl(source))}
      />
      <PageTab
        title={
          <UserThemeConsumer>
            {(theme) => (
              <StyledErrorTitleDiv>
                <Icon color={theme.palette.orange.shade40} name="warningFilledSmall" />
                <Trans id="crm-syncs-page-errors-tab-title">Errors</Trans>
              </StyledErrorTitleDiv>
            )}
          </UserThemeConsumer>
        }
        isSelected={isOnErrorsPage}
        onClick={() => navigateToLink(buildContactsManagerSyncsUrl(source, true))}
      />
    </>
  );
}

function renderFooter(
  canSyncOrSave: boolean,
  isAccountSyncEnabledOnServer: boolean,
  saveAndSync: AsyncHandler,
  navigateToPreviousPage: Handler,
) {
  return (
    <ButtonGroup>
      <Button isRounded onClick={navigateToPreviousPage}>
        <Trans id="crm-sync-page-cancel-button">Cancel</Trans>
      </Button>
      <Button
        isRounded
        isDisabled={!canSyncOrSave}
        variant={ButtonVariantsEnum.PRIMARY}
        onClick={async () => {
          await saveAndSync();
          navigateToPreviousPage();
        }}
      >
        {/* When editing an existing sync we show the text as save changes */}
        {isAccountSyncEnabledOnServer ? (
          <Trans id="crm-sync-page-save-button">Save changes</Trans>
        ) : (
          <Trans id="crm-sync-page-sync-button">Sync</Trans>
        )}
      </Button>
    </ButtonGroup>
  );
}

function renderInfoBoxes(
  source: CrmSyncSourcesEnum,
  helpCenterLink: string,
  displayName: string,
  showNextSync: boolean,
  canSyncNow: boolean,
  onSync: Handler,
) {
  return (
    <StyledInfoBoxWrapperDiv>
      <CrmSyncsScheduleInfoBoxContainer
        onSync={onSync}
        source={source}
        showNextSync={showNextSync}
        canSyncNow={canSyncNow}
      />
      <InfoBox
        onFooterClick={() => {
          openInBrowser(helpCenterLink);
        }}
        title={<Trans id="crm-sync-page-info-box-title">1-way sync from {displayName} to Front</Trans>}
        footer={<Trans id="crm-sync-page-infobox-learn-more-link">Learn more</Trans>}
      >
        <Trans id="crm-sync-page-infobox-content">
          Automatically syncs once per day. You can also manually trigger a sync at any time from this page.
        </Trans>
      </InfoBox>
    </StyledInfoBoxWrapperDiv>
  );
}

function computeCanSync(isUpdatingCrmSync: boolean, isCrmSyncLoaded: boolean, formState?: CrmSyncsFormModel) {
  return !isUpdatingCrmSync && isCrmSyncLoaded && computeIsFormValid(formState);
}

function computeIsSyncEnabled(isSyncEnabledOnServer: boolean, formState?: CrmSyncsFormModel) {
  if (!formState) {
    return isSyncEnabledOnServer;
  }
  return Boolean(formState?.isEnabled);
}

function computeIsFormValid(formState?: CrmSyncsFormModel) {
  // if the form is disabled consider it valid
  if (formState && !formState.isEnabled) {
    return true;
  }
  return isListViewValid(formState) && isMappingValid(formState);
}

function computeShouldShowNextSync(
  isAccountSyncEnabledOnServer: boolean,
  isCardSyncEnabledOnServer: boolean,
  updatedSettingFormState?: CrmSyncSettingsFormModel,
) {
  if (updatedSettingFormState === undefined) {
    return isAccountSyncEnabledOnServer || isCardSyncEnabledOnServer;
  }
  return (
    Boolean(updatedSettingFormState.account?.isEnabled) || Boolean(updatedSettingFormState.card?.isEnabled)
  );
}
